/**
 * Which tool names the user should be shown.
 *
 * Tool activity surfaces in two places, fed by two different sources: the
 * thinking line above the composer (client `onToolCall` in use-chat.ts) and the
 * pills on a finished message (backend `data-crucible-tool-status` stream
 * parts). They had drifted — the pill path filtered a `system` sentinel, the
 * thinking line filtered nothing — so the rule lives here once and both call it.
 *
 * The distinction is whether the name describes work the user asked for.
 * `ctgov_search` did something and has a result worth naming. `ready_to_answer`
 * fetches nothing: it exists so the model can leave the tool loop early, and its
 * only effect is server-side (the following step gets `toolChoice: 'none'` plus
 * a nudge to write the answer — see chat-v3/tool-logging.ts). Rendering it as
 * "READY TO ANSWER" told the user about our control flow, not their request.
 *
 * `route_handoff` is deliberately NOT here. It is also a pure signal, but it
 * means something to the user — the work moved to another persona — and it
 * renders its own card.
 */

/**
 * Canonical name of the early-exit signal, defined here because three places
 * need it: the tool definition (tools.ts), the loop control that reacts to it
 * (chat-v3/tool-logging.ts), and this filter. A string literal in each would be
 * three chances to drift.
 */
export const READY_TO_ANSWER_TOOL_NAME = 'ready_to_answer'

/**
 * Names that exist to steer the loop rather than to do work.
 *
 * `system` is the backend's sentinel for status messages that aren't a tool at
 * all; it was already excluded at the pill site and is folded in so there is a
 * single definition rather than two half-rules.
 */
export const CONTROL_SIGNAL_TOOLS: ReadonlySet<string> = new Set([
  READY_TO_ANSWER_TOOL_NAME,
  'system',
])

/**
 * True when this tool should appear in the thinking line and the pill bar.
 *
 * Anything that isn't a non-empty string is treated as not visible: these names
 * arrive from stream parts, and a malformed one should stay off the screen
 * rather than render as an empty or `[object Object]` pill.
 */
export function isUserVisibleTool(toolName: unknown): boolean {
  if (typeof toolName !== 'string' || !toolName) return false
  return !CONTROL_SIGNAL_TOOLS.has(toolName)
}

/**
 * Filter a list of executed tools down to the ones worth showing.
 *
 * This belongs at the boundary where the list becomes what the user sees — the
 * point where it enters `messageToolsMap` — and not at the producers. Five
 * separate code paths push into the executed-tools ref (client tool calls,
 * route handoffs, backend status parts, restored history, retries), and
 * filtering one of them left `ready_to_answer` rendering from the other four.
 * Confirmed live in the rig on 2026-08-13, after exactly that mistake.
 */
export function visibleTools<T extends { name?: unknown }>(tools: unknown): T[] {
  if (!Array.isArray(tools)) return []
  return (tools as T[]).filter((tool) => isUserVisibleTool(tool?.name))
}
